import React from 'react';

export const MEDCALL_SESSION_LIMIT_SECONDS = 600;
export const MEDCALL_SESSION_WARNING_SECONDS = 30;

type MedcallSessionState = 'active' | 'warning' | 'expired';

type MedcallSessionTimeoutProps = {
  inactivitySeconds?: number;
  onLogout: () => void;
  warningSeconds?: number;
};

export const formatMedcallSessionCountdown = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
};

export const MedcallSessionTimeout = ({
  inactivitySeconds = MEDCALL_SESSION_LIMIT_SECONDS,
  onLogout,
  warningSeconds = MEDCALL_SESSION_WARNING_SECONDS,
}: MedcallSessionTimeoutProps) => {
  const [sessionState, setSessionState] = React.useState<MedcallSessionState>('active');
  const [countdown, setCountdown] = React.useState(warningSeconds);
  const warningTimer = React.useRef<number | null>(null);

  const scheduleWarning = React.useCallback(() => {
    if (warningTimer.current !== null) {
      window.clearTimeout(warningTimer.current);
    }

    warningTimer.current = window.setTimeout(() => {
      setCountdown(warningSeconds);
      setSessionState('warning');
    }, inactivitySeconds * 1000);
  }, [inactivitySeconds, warningSeconds]);

  React.useEffect(() => {
    const activityEvents = ['mousemove', 'keydown', 'mousedown', 'touchstart'] as const;
    activityEvents.forEach((eventName) => window.addEventListener(eventName, scheduleWarning));
    scheduleWarning();

    return () => {
      if (warningTimer.current !== null) {
        window.clearTimeout(warningTimer.current);
      }
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, scheduleWarning));
    };
  }, [scheduleWarning]);

  React.useEffect(() => {
    if (sessionState !== 'warning') {
      return;
    }

    const countdownTimer = window.setInterval(() => {
      setCountdown((currentCountdown) => {
        if (currentCountdown <= 1) {
          setSessionState('expired');
          return 0;
        }

        return currentCountdown - 1;
      });
    }, 1000);

    return () => window.clearInterval(countdownTimer);
  }, [sessionState]);

  const keepSession = () => {
    setCountdown(warningSeconds);
    setSessionState('active');
    scheduleWarning();
  };

  if (sessionState === 'active') {
    return null;
  }

  if (sessionState === 'warning') {
    return (
      <div aria-modal="true" className="medcall-session-modal" role="dialog">
        <div className="medcall-session-modal__content">
          <p className="medcall-session-modal__message">Your session is about to expire in :</p>
          <div className="medcall-session-modal__countdown">
            {formatMedcallSessionCountdown(countdown)}
          </div>
          <p className="medcall-session-modal__question">Are you still there?</p>
          <button className="medcall-session-modal__button" onClick={keepSession} type="button">
            KEEP ME LOGGED IN
          </button>
        </div>
      </div>
    );
  }

  return (
    <div aria-modal="true" className="medcall-session-modal" role="dialog">
      <div className="medcall-session-modal__content">
        <p className="medcall-session-modal__message">Session expired</p>
        <button className="medcall-session-modal__button" onClick={onLogout} type="button">
          PLEASE SIGN IN
        </button>
      </div>
    </div>
  );
};
